import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SectionCard from "../../components/SectionCard";
import SectionRow from "../../components/SectionRow";
import {
    canManageLevels,
    canManageLocations,
    getPrimaryActiveResource,
    hasManagingRole,
} from "utils/helpers/user";
import { renderPaymentType } from "utils/helpers/job";
import { renderSettingsResourceLabel } from "utils/helpers/settings";

function CompanyInformation({ user }: any) {
    const { t } = useTranslation();

    const primaryLocation = useMemo(
        () => getPrimaryActiveResource(user?.locations || []),
        [user?.locations],
    );

    const otherLocations = useMemo(
        () => (user?.locations || []).filter((l: any) => !l.primary && l.status === "active"),
        [user?.locations],
    );

    const hierarchyGroup = useMemo(
        () => (user?.orgLevelGroups as any[])?.find(({ hasHierarchy }: any) => hasHierarchy),
        [user?.orgLevelGroups],
    );

    const isManager = user && hierarchyGroup && hasManagingRole(user.roles);
    const manageAbility = user?.manageAbility;

    return (
        <SectionCard title={t("company-information")}>
            <SectionRow
                label={t("company-name")}
                values={user?.companies?.map(({ name }: any) => name) ?? []}
            />
            <SectionRow
                label={t("location")}
                value={primaryLocation ? renderSettingsResourceLabel(primaryLocation) : undefined}
            />
            <SectionRow
                label={t("other-locations")}
                values={otherLocations?.map(renderSettingsResourceLabel) ?? []}
            />
            <SectionRow
                label={t("pay-group")}
                value={user?.payGroup ? renderSettingsResourceLabel(user.payGroup) : undefined}
            />
            <SectionRow
                label={t("calculation-group")}
                value={user?.calculationGroup ? renderSettingsResourceLabel(user.calculationGroup) : undefined}
            />
            <SectionRow
                label={t("allocated-holiday")}
                value={user?.holiday ? renderSettingsResourceLabel(user.holiday) : undefined}
            />
            <SectionRow
                label={t("roles")}
                values={user?.roles?.map(renderSettingsResourceLabel) ?? []}
            />

            {isManager && (
                <>
                    <SectionRow
                        label={t("manage-ability")}
                        value={manageAbility ? t(manageAbility) : undefined}
                    />
                    {manageAbility && canManageLevels(manageAbility) && (
                        <SectionRow
                            label={t("manage-levels")}
                            values={user?.managedLevels?.map(renderSettingsResourceLabel) ?? []}
                        />
                    )}
                    {manageAbility && canManageLocations(manageAbility, hierarchyGroup) && (
                        <SectionRow
                            label={t("manage-locations")}
                            values={user?.locations?.map(renderSettingsResourceLabel) ?? []}
                        />
                    )}
                    {manageAbility === "direct-users-and-pay-groups" && (
                        <SectionRow
                            label={t("manage-pay-groups")}
                            values={user?.managedPayGroups?.map(renderSettingsResourceLabel) ?? []}
                        />
                    )}
                    {manageAbility === "direct-users-and-calculation-group" && (
                        <SectionRow
                            label={t("manage-calculation-group")}
                            values={user?.managedCalcGroups?.map(renderSettingsResourceLabel) ?? []}
                        />
                    )}
                    {(manageAbility === "direct-users-and-organisation-level-and-project" ||
                        manageAbility === "direct-project") && (
                        <SectionRow
                            label={t("manage-level-and-project")}
                            values={user?.managedProjects?.map(renderSettingsResourceLabel) ?? []}
                        />
                    )}
                </>
            )}

            <SectionRow
                label={t("additional-levels")}
                values={user?.additionalLevels?.map(renderSettingsResourceLabel) ?? []}
            />
            <SectionRow
                label={t("hourly-or-salary")}
                value={user?.salaryOrHourly ? t(renderPaymentType(user.salaryOrHourly)) : undefined}
            />
        </SectionCard>
    );
}

export default CompanyInformation;
